import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import {
  Menu,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Sidebar as ShadSidebar,
  SidebarProvider as ShadSidebarProvider,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarTrigger as ShadSidebarTrigger,
} from "@/components/ui/sidebar";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import * as Icons from "@/assets/features/sidebar";
import { useEffectiveDocumentDir } from "@/hooks/useEffectiveDocumentDir";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

// --- Internal Context -----------------------------------------------
const LocalSidebarContext = createContext(null);


export function useLocalSidebar() {
  const ctx = useContext(LocalSidebarContext);
  if (!ctx)
    throw new Error("useLocalSidebar must be used inside SidebarProvider");
  return ctx;
}

// --- Provider -------------------------------------------------------


export function SidebarProvider({
  children,
  defaultCollapsed = false,
  storageKey = "app:sidebar:collapsed",
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      if (!storageKey || typeof window === "undefined") return defaultCollapsed;

      const raw = localStorage.getItem(storageKey);
      return raw == null ? defaultCollapsed : raw === "true";
    } catch (e) {
      // In case localStorage is blocked or throws an error
      console.error("Error reading localStorage:", e);
      return defaultCollapsed;
    }
  });

  useEffect(() => {
    try {
      if (!storageKey || typeof window === "undefined") return;
      localStorage.setItem(storageKey, String(collapsed));
    } catch (e) {
    }
  }, [collapsed, storageKey]);

  const value = useMemo(() => ({ collapsed, setCollapsed }), [collapsed]);

  return (
    <ShadSidebarProvider>
      <LocalSidebarContext.Provider value={value}>
        {children}
      </LocalSidebarContext.Provider>
    </ShadSidebarProvider>
  );
}

// --- Reusable Sidebar Layout ---------------------------------------

export function SidebarLayout({
  items, // Array of sidebar items/groups

  className = "",
  width = 210,
  miniWidth = 60,
}) {
  const { t, i18n } = useTranslation();
  const { collapsed, setCollapsed } = useLocalSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const docDir = useEffectiveDocumentDir();

  // Close the mobile drawer when the viewport grows to desktop size
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handleChange = (e) => e.matches && setMobileOpen(false);
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  return (
    <div className={cn("relative flex min-h-0 shrink-0 flex-col", className)}>
      {/*====================== Desktop ======================*/}
      <aside className="hidden h-full shrink-0 md:block" aria-hidden={false}>
        <motion.div
          initial={false}
          animate={{ width: collapsed ? miniWidth : width }}
          transition={{ duration: 0.18, ease: "easeInOut" }}
          dir={docDir}
          className="flex h-screen flex-col overflow-hidden border-sidebar-border bg-sidebar "
        >
          {/* Logo and Collapse Toggle */}
          <div className="flex items-center justify-between px-3 py-4">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0">
                {!collapsed ? (
                  <img
                    src={Icons.FullLogo}
                    className="h-auto w-40"
                    alt="Full Logo"
                  />
                ) : (
                  <Icons.MiniLogo className="w-[4rem] h-9" />
                )}
              </div>
            </div>

            {/* Collapse Button */}
            <Button
              size="icon"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={
                collapsed ? t("sidebar.expandSidebar") : t("sidebar.collapseSidebar")
              }
              className={`rounded-full z-50 absolute h-6 w-6 -end-3 ${
                collapsed ? "top-20" : "top-24"
              }`}
            >
              <span className="inline-flex">
                {docDir === "rtl" ? (
                  collapsed ? (
                    <ChevronLeft className="rounded-full bg-primary text-white" />
                  ) : (
                    <ChevronRight className="rounded-full bg-primary text-white" />
                  )
                ) : collapsed ? (
                  <ChevronRight className="rounded-full bg-primary text-white" />
                ) : (
                  <ChevronLeft className="rounded-full bg-primary text-white" />
                )}
              </span>
            </Button>
          </div>

          {/* Menu Items */}
          <ScrollArea
            dir={docDir}
            className={`flex-1 ltr:pl-2 rtl:pr-2 mt-10 h-[60vh] ${collapsed ? "" : "scroll-y"}`}
          >
            <TooltipProvider>
              <nav>
                {items.map((it) => (
                  <SidebarGroup key={it.label}>
                    {/* Group Label */}
                    {it.label && (
                      <SidebarGroupLabel
                        className={`text-ms flex items-center gap-2 font-semibold text-[#7F8BA0] justify-start text-start ${!collapsed ? "pt-5" : ""
                          }`}
                      >
                        {(!collapsed || (collapsed && !it.children)) && it.icon}
                        <span
                          className={`${collapsed ? "sr-only" : "block"
                            } text-xs font-semibold`}
                        >
                          {it.label}
                        </span>
                      </SidebarGroupLabel>
                    )}

                    {/* Group Content (Items) */}
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {it.children && it.children.length > 0
                          ? it.children.map((sub) => (
                            <SidebarItem
                              key={sub.label}
                              item={sub}
                              collapsed={collapsed}
                              showLabelWhenCollapsed={false}
                              level={1}
                            />
                          ))
                          : it.path && (
                            <SidebarItem
                              item={it}
                              collapsed={collapsed}
                              showLabelWhenCollapsed={false}
                              level={1}
                            />
                          )}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                ))}
              </nav>
            </TooltipProvider>
          </ScrollArea>
        </motion.div>
      </aside>

      {/* ====================== Mobile ======================*/}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side={docDir === "rtl" ? "right" : "left"}
          dir={docDir}
          className="flex h-dvh w-[280px] max-w-[85vw] flex-col gap-0 bg-sidebar p-0 [&>button:last-child]:top-6 rtl:[&>button:last-child]:left-4 rtl:[&>button:last-child]:right-auto"
        >
          <SheetTitle className="sr-only">{t("sidebar.menu", "Menu")}</SheetTitle>
          <SheetDescription className="sr-only">
            {t("sidebar.navigation", "Navigation")}
          </SheetDescription>

          {/* Logo */}
          <div className="flex shrink-0 items-center px-4 py-4">
            <img src={Icons.FullLogo} className="h-auto w-36" alt="Full Logo" />
          </div>

          {/* Menu Items */}
          <div className="scroll-y min-h-0 flex-1 px-2 pb-6">
            <nav>
              {items.map((it) => (
                <SidebarGroup key={it.label} className="py-1">
                  {it.label && (
                    <SidebarGroupLabel className="flex items-center gap-2 pt-3 text-start text-xs font-semibold text-[#7F8BA0]">
                      {it.icon}
                      <span>{it.label}</span>
                    </SidebarGroupLabel>
                  )}
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {it.children && it.children.length > 0
                        ? it.children.map((sub) => (
                          <SidebarItem
                            key={sub.label}
                            item={sub}
                            collapsed={false}
                            showLabelWhenCollapsed
                            onClick={() => setMobileOpen(false)}
                            level={1}
                          />
                        ))
                        : it.path && (
                          <SidebarItem
                            item={it}
                            collapsed={false}
                            showLabelWhenCollapsed
                            onClick={() => setMobileOpen(false)}
                            level={1}
                          />
                        )}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </nav>
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile toggle button (usually placed in topbar of app) */}
      <div className="md:hidden">
        <Button
          size="icon"
          variant="outline"
          onClick={() => setMobileOpen(true)}
          className="m-3"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

// --- SidebarItem  --------------------------------------

const pathOnly = (path) => String(path ?? "").split(/[?#]/)[0];

function queryMatches(path, search) {
  const query = String(path ?? "").split("?")[1];
  if (!query) return true;
  const current = new URLSearchParams(search);
  return [...new URLSearchParams(query)].every(([key, value]) => current.get(key) === value);
}

function pathnameIsAlreadyUnderGroup(pathname, groupBasePath) {
  if (!groupBasePath) return false;
  const path = pathname.split(/[?#]/)[0] ?? "";
  return path === groupBasePath || path.startsWith(`${groupBasePath}/`);
}


export function SidebarItem({
  item,
  collapsed,
  showLabelWhenCollapsed = false,
  onClick,
  level = 1,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const hasSubitems = item.subitems && item.subitems.length > 0;
  const [isExpanded, setIsExpanded] = useState(false);
  const prevSubitemRouteActiveRef = useRef(false);


 
  const isParentActive = useMemo(() => {
    if (!item.path) return false;
    return location.pathname.includes(item.path);
  }, [location.pathname, item.path]);

  const isSubitemActive = useMemo(() => {
    if (!hasSubitems) return false;
    return item.subitems?.some((s) =>
      location.pathname.includes(pathOnly(s.path))
    );
  }, [location.pathname, item.subitems, hasSubitems]);

  const isCurrentItemActive = useMemo(() => {
    if (!item.path) return false;
    return (
      location.pathname === pathOnly(item.path) &&
      queryMatches(item.path, location.search)
    );
  }, [location.pathname, location.search, item.path]);


  const expandedProbeRef = useRef(isExpanded);

  useEffect(() => {
    expandedProbeRef.current = isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    if (!hasSubitems) {
      prevSubitemRouteActiveRef.current = false;
      return;
    }

    const prevStored = prevSubitemRouteActiveRef.current;
    const becameRouteActiveUnderGroup =
      isSubitemActive && !prevStored;
    prevSubitemRouteActiveRef.current = isSubitemActive;

    if (becameRouteActiveUnderGroup) {
      setIsExpanded(true);
    }
  }, [hasSubitems, isSubitemActive]);

  const Icon = item.icon;
  const { setCollapsed } = useLocalSidebar();
  const { i18n } = useTranslation();
  const collapsedTooltipSide = i18n.dir() === "rtl" ? "left" : "right";
  const isRtlSidebar = i18n.dir() === "rtl";

  const handleClick = () => {
    // Collapsed sidebar hides sub-items, so open it and show the group instead.
    if (collapsed && hasSubitems) {
      setCollapsed(false);
      setIsExpanded(true);
      return;
    }

    const collapsingHeadingWithSubmenus =
      hasSubitems && expandedProbeRef.current;

    if (hasSubitems) {
      setIsExpanded((prev) => !prev);
    }

    if (item.path) {
      let shouldNavigateToParentRoute = !collapsingHeadingWithSubmenus;

      if (shouldNavigateToParentRoute && hasSubitems) {
        if (
          pathnameIsAlreadyUnderGroup(location.pathname, item.path)
        ) {
          shouldNavigateToParentRoute = false;
        }
      }

      if (shouldNavigateToParentRoute) {
        navigate(item.path);
      }

      if (!hasSubitems && !onClick) {
        setCollapsed(true);
      }
    }

    if (onClick && !hasSubitems) onClick();
  };

  // =========================
  // ACTIVE CLASS PER LEVEL
  // =========================
  const activeClass =
    level === 1
      ? isParentActive || isSubitemActive
        ? "bg-accent-active"
        : "hover:bg-accent-hover"
      : isCurrentItemActive
      ? "!text-primary font-medium"
      : "hover:text-primary/80";

  // =========================
  // MAIN ITEM RENDER
  // =========================
  const content = (
    <div
      className={`flex items-center justify-between px-2 py-2 rounded-md cursor-pointer 
        ${level > 1 ? "ms-12" : ""}
        ${activeClass}
      `}
      role="button"
      tabIndex={0}
      onClick={handleClick}
    >
      <div className="flex items-center gap-3">
        {/* Icon only for parent items */}
        {level === 1 && (
          <div
            className={`p-[0.81rem] flex-shrink-0 ${
              isParentActive || isSubitemActive ? "bg-primary rounded-[1rem] shadow-sidebar" : ""
            }`}
          >
            {Icon && (
              <Icon
                className="w-5 h-5"
                color={isParentActive || isSubitemActive ? "var(--color-white)" : "var(--color-blue-500)"}
              />
            )}
          </div>
        )}

        {/* Label */}
        <div
          className={`${
            collapsed && !showLabelWhenCollapsed ? "sr-only" : "block"
          } ${level > 1 && isCurrentItemActive ? "text-primary" : ""}
          `}
        >
          <p className="text-md font-medium text-start">{item.label}</p>
        </div>
      </div>

      {/* Chevron for expandable parents */}
      {!collapsed && hasSubitems && (
        <motion.div
          animate={{
            rotate: isRtlSidebar ? (isExpanded ? -90 : 0) : isExpanded ? 90 : 0,
          }}
          transition={{ duration: 0.15 }}
          className="p-1"
        >
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </motion.div>
      )}
    </div>
  );

  return (
    <>
      {/* Tooltip in collapsed mode */}
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side={collapsedTooltipSide}>
            <p>{item.label}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        content
      )}

      {/* Subitems */}
      {!collapsed && hasSubitems && isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.15 }}
          className="ps-2"
        >
          <SidebarMenu>
            {item.subitems.map((subitem) => (
              <SidebarItem
                key={subitem.label}
                item={subitem}
                collapsed={collapsed}
                showLabelWhenCollapsed={true}
                level={level + 1}
                onClick={onClick}
              />
            ))}
          </SidebarMenu>
        </motion.div>
      )}
    </>
  );
}


// ============= Trigger (shadcn-compatible) ============

export function SidebarTrigger({ className = "" }) {
  const { t, i18n } = useTranslation();
  const { collapsed, setCollapsed } = useLocalSidebar();
  const isRtl = i18n.dir() === "rtl";

  const triggerIcon =
    isRtl ? (
      collapsed ? (
        <ChevronLeft className="h-4 w-4 rounded-full bg-primary text-white" />
      ) : (
        <ChevronRight className="h-4 w-4 rounded-full bg-primary text-white" />
      )
    ) : collapsed ? (
      <ChevronRight className="h-4 w-4 rounded-full bg-primary  text-white" />
    ) : (
      <ChevronLeft className="h-4 w-4 rounded-full bg-primary text-white" />
    );

  return (
    <ShadSidebarTrigger
      className={className}
      onClick={() => setCollapsed((c) => !c)}
      aria-label={
        collapsed ? t("sidebar.expandSidebar") : t("sidebar.collapseSidebar")
      }
    >
      <span className="inline-flex">{triggerIcon}</span>
    </ShadSidebarTrigger>
  );
}